import { Global, css } from '@emotion/react';
import { FC, useEffect, useRef } from 'react';

const ZALO_WIDGET_SELECTOR = '.zalo-chat-widget';
export const ZALO_BUTTON_SIZE = 60;

const COLUMN_ICON_SIZE = 50;

export const ZALO_LIFT_REM = 3.6;

const ZALO_WIDGET_SCALE = COLUMN_ICON_SIZE / ZALO_BUTTON_SIZE;

const zaloWidgetPinStyle = css`
  .zalo-chat-widget {
    right: var(--zalo-oa-x, 2rem) !important;
    bottom: var(--zalo-oa-y, 2rem) !important;
    transform: scale(var(--zalo-oa-scale, ${ZALO_WIDGET_SCALE})) !important;
    transform-origin: bottom right !important;
  }
`;

interface ZaloOASlotProps {
  variant: 'column' | 'fixed';
  present: boolean;
  bottom?: number;
  right?: number;
}

export const ZaloOASlot: FC<ZaloOASlotProps> = ({
  variant,
  present,
  bottom = 2,
  right = 2.5,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!present) return;
    const root = document.documentElement;
    let raf = 0;
    const sync = () => {
      raf = 0;
      const anchor = anchorRef.current;
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();

      root.style.setProperty(
        '--zalo-oa-x',
        `${Math.round(root.clientWidth - r.right)}px`,
      );
      root.style.setProperty(
        '--zalo-oa-y',
        `${Math.round(root.clientHeight - r.bottom)}px`,
      );

      const widget = document.querySelector(ZALO_WIDGET_SELECTOR);
      const collapsed =
        !widget ||
        widget.getBoundingClientRect().height <= ZALO_BUTTON_SIZE + 8;
      root.style.setProperty(
        '--zalo-oa-scale',
        collapsed ? `${ZALO_WIDGET_SCALE}` : '1',
      );
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(sync);
    };
    sync();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    const ro = new ResizeObserver(schedule);
    const widget = document.querySelector(ZALO_WIDGET_SELECTOR);
    if (widget) ro.observe(widget);
    if (anchorRef.current) {
      ro.observe(anchorRef.current);
      if (anchorRef.current.parentElement) {
        ro.observe(anchorRef.current.parentElement);
      }
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);

      root.style.removeProperty('--zalo-oa-x');
      root.style.removeProperty('--zalo-oa-y');
      root.style.removeProperty('--zalo-oa-scale');
    };
  }, [present]);

  if (!present) return null;

  return (
    <>
      <Global styles={zaloWidgetPinStyle} />
      {variant === 'column' ? (
        <div
          ref={anchorRef}
          aria-hidden
          style={{
            width: COLUMN_ICON_SIZE,
            height: COLUMN_ICON_SIZE,
            pointerEvents: 'none',
          }}
        />
      ) : (
        <div
          ref={anchorRef}
          aria-hidden
          style={{
            position: 'fixed',
            right: `${right}rem`,
            bottom: `${bottom}rem`,
            width: COLUMN_ICON_SIZE,
            height: COLUMN_ICON_SIZE,
            pointerEvents: 'none',
            zIndex: 1030,
          }}
        />
      )}
    </>
  );
};
